/**
 * Central source of truth for how backend API URLs are composed in the frontend.
 *
 * Convention for every HTTP service (follow this for new services too):
 *   1. Inject `ApiUrlService` (never `ENVIRONMENT_CONFIG` directly).
 *   2. Build the service base URL once via `apiUrlService.build(ApiService.X)`.
 *   3. Compose endpoint paths from `ApiRoute` segments instead of retyping
 *      string literals, e.g. `` `${base}/${ApiRoute.Projects}/${id}` ``.
 *
 * Do NOT concatenate `environmentConfig.endpoint + 'ServiceName'` in a service.
 */

type ValueOf<T> = T[keyof T];

/**
 * Backend service base paths as exposed through the reverse proxy (Traefik).
 *
 * These values are case-sensitive and must match the backend route prefixes
 * exactly, because Traefik matches routes case-sensitively. Keep every service
 * term defined here once so it is never duplicated (and mistyped) across the
 * codebase.
 */
export const ApiService = {
  DataMaintenance: 'DataMaintenance',
  ProjectProductManagement: 'ProjectProductManagement',
  UserPermissionManagement: 'UserPermissionManagement',
} as const;

export type ApiService = ValueOf<typeof ApiService>;

/**
 * Shared resource route segments that appear at more than one call site (across
 * services or repeated within a service). Centralized so a segment like
 * `projects` is defined once rather than retyped as a magic string everywhere.
 *
 * One-off, endpoint-specific segments (e.g. `duplicate`, `calculate`,
 * `reportset`) are intentionally kept inline at their single call site.
 */
export const ApiRoute = {
  // ProjectProductManagement
  Projects: 'projects',
  Products: 'products',
  Groups: 'groups',
  Reports: 'reports',
  // UserPermissionManagement
  Users: 'users',
  Permissions: 'permissions',
  Grants: 'grants',
  // DataMaintenance
  Assets: 'assets',
  UserAssets: 'user-assets',
  Worktrees: 'worktrees',
} as const;

export type ApiRoute = ValueOf<typeof ApiRoute>;
